/* **The choice row** — one option of a "pick exactly one" list, drawn as a row with its own radio.
 *
 * A stylesheet with no component, the same shape as `splitDialog.module.css` beside it: the rows'
 * CONTENT differs on every surface (a client and an e-mail, a delivery mode and a glyph, a template
 * and its cover), and what is shared is the row itself — the radio mark, the chosen wash, the
 * hairline between neighbours, the focus ring and the neutral badge.
 *
 * Promoted out of `features/assign-plan/ui/AssignPlanDialog.module.css` on 23 September 2026, on its
 * second consumer: „Nowy program"'s template list, whose chosen row must read exactly as the assign
 * dialog's chosen client does. Every declaration below is that file's, moved unchanged.
 *
 * ── Three tracks: the mark, the content, the trailing badge ─────────────────────────────────
 * A surface with more than one leading element (a glyph, a thumbnail) wraps them in its own box in
 * the middle track rather than adding a column here.
 *
 * ── What the surface declares ───────────────────────────────────────────────────────────────
 * What stands inside the row, and nothing this file states. A declaration written on both sides is
 * settled by chunk order, which differs between the dev server and the build.
 *
 * Every colour, space, radius and type value is a token; heights are layout. */

.rows {
  display: flex;
  flex-direction: column;
}

.row {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-assign-row);
  padding: var(--space-6px) var(--space-10px);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.row + .row {
  border-top: var(--border-hairline) solid var(--border);
  border-radius: var(--space-0);
}

/* The chosen option wears the app's own selection: an `--accent-soft` wash under an accent edge,
 * the treatment the plan library's mode option already uses (`pages/plan-library/ui/FilterPanel`).
 * A wash and an edge, never a glow — and the accent stays a scalpel, because it marks only what the
 * trainer has CHOSEN. */
.rowSelected {
  background: var(--accent-soft);
  box-shadow: var(--shadow-row-chosen);
}

.rowMark {
  width: var(--space-14px);
  height: var(--space-14px);
  border: var(--border-thick) solid var(--border-3);
  border-radius: var(--r-pill);
}

.rowSelected .rowMark {
  background: var(--fill-radio-chosen);
  border-color: var(--accent);
}

/* The ink variant of the chosen row, as the booking dialogs draw it (T-C6 and the client card's
 * settings): a 2px ink ring and an ink dot, no wash under it — where the accent is spent on the
 * act that sends the choice. */
.rowChosenInk {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--outline-inset);
}

.rowChosenInk .rowMark {
  background: var(--text);
  border-color: var(--text);
}

.rowText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: var(--space-0);
}

.rowTitle {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.rowMeta {
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* Neutral, always: the system a program runs on is a property of the row, not a state anybody has
 * to act on. */
.rowBadge {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: var(--h-assign-badge);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

/* The native radio does the work — roving focus, arrows, `:checked` — off screen, wearing
 * `shared/ui/visuallyHiddenControl`. `.row` is the positioned ancestor it is placed against, and
 * the ring is drawn on the row because that is the control the trainer can see. */
.row:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}
